import { type FormEvent, useState } from "react";

import { ApiError } from "@/api/client";
import { useChangePassword } from "@/api/hooks";
import { useAuth } from "@/auth/AuthProvider";
import { Layout } from "@/components/Layout";
import { OAuthAppsSection } from "@/components/OAuthAppsCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function SettingsPage() {
	const { admin } = useAuth();
	const change = useChangePassword();
	const [current, setCurrent] = useState("");
	const [next, setNext] = useState("");
	const [confirm, setConfirm] = useState("");
	const [success, setSuccess] = useState(false);

	const confirmMismatch = confirm.length > 0 && next !== confirm;
	const tooShort = next.length > 0 && next.length < 12;

	async function onSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setSuccess(false);
		if (confirmMismatch || tooShort) return;
		try {
			await change.mutateAsync({ current_password: current, new_password: next });
			setCurrent("");
			setNext("");
			setConfirm("");
			setSuccess(true);
		} catch {
			// shown via change.error
		}
	}

	const errorMessage =
		change.error instanceof ApiError
			? change.error.detail
			: change.error
				? "Unable to change password."
				: null;

	return (
		<Layout>
			<div className="mx-auto max-w-2xl space-y-8">
				<header>
					<h2 className="text-2xl font-semibold tracking-tight">Settings</h2>
					<p className="text-sm text-muted-foreground">
						Signed in as <span className="font-mono">{admin?.email}</span>
					</p>
				</header>

				<Card>
					<CardHeader>
						<CardTitle>Change password</CardTitle>
						<CardDescription>
							Must be at least 12 characters. Does not sign out other sessions — log out separately
							if you need to invalidate them.
						</CardDescription>
					</CardHeader>
					<CardContent>
						<form className="space-y-4" onSubmit={onSubmit} noValidate>
							<div className="space-y-2">
								<Label htmlFor="current">Current password</Label>
								<Input
									id="current"
									type="password"
									autoComplete="current-password"
									required
									value={current}
									onChange={(e) => setCurrent(e.target.value)}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="new">New password</Label>
								<Input
									id="new"
									type="password"
									autoComplete="new-password"
									minLength={12}
									required
									value={next}
									onChange={(e) => setNext(e.target.value)}
								/>
								{tooShort ? (
									<p className="text-xs text-destructive">Must be at least 12 characters.</p>
								) : null}
							</div>
							<div className="space-y-2">
								<Label htmlFor="confirm">Confirm new password</Label>
								<Input
									id="confirm"
									type="password"
									autoComplete="new-password"
									required
									value={confirm}
									onChange={(e) => setConfirm(e.target.value)}
								/>
								{confirmMismatch ? (
									<p className="text-xs text-destructive">Passwords don't match.</p>
								) : null}
							</div>
							{errorMessage ? (
								<p className="text-sm text-destructive" role="alert">
									{errorMessage}
								</p>
							) : null}
							{success ? (
								<output className="block text-sm text-emerald-600">Password updated.</output>
							) : null}
							<Button
								type="submit"
								disabled={change.isPending || tooShort || confirmMismatch || !current || !next}
							>
								{change.isPending ? "Updating…" : "Update password"}
							</Button>
						</form>
					</CardContent>
				</Card>

				<OAuthAppsSection />
			</div>
		</Layout>
	);
}
